// The person's memory, as they see it: long-term (always present), short-term (what the assistant noticed, with how
// close it is to becoming permanent), proposals learnt from third-party content, and the consolidation diary.
import { api } from "../api.ts";
import { icon } from "../icons.ts";
import { relative } from "../format.ts";
import { $, $$, busy, empty, errorText, esc, load, skeleton, toast } from "../ui.ts";
import type { Memory, MemoryNote, MemoryRun } from "../types.ts";

const KIND: Record<string, string> = { preference: "Preferencia", routine: "Rutina", person: "Persona", project: "Proyecto", fact: "Dato", other: "Otro" };
const SOURCE: Record<Memory["source"], string> = { user: "se lo dijiste tú", admin: "lo añadió administración", conversation: "se lo pediste en una conversación", consolidation: "lo consolidó por la noche" };
const dots = (n: number) => `<span class="dots" role="img" aria-label="Importancia ${n} de 5">${[1, 2, 3, 4, 5].map((i) => `<i class="${i <= n ? "on" : ""}"></i>`).join("")}</span>`;

export function mountMemory(box: HTMLElement, appName: string) {
  box.innerHTML = `
    <p class="ink-2 memory-intro">${esc(appName)} recuerda en tres niveles: lo que habláis ahora, lo que va notando (<strong>en observación</strong>) y lo que ya sabe de ti
      (<strong>permanente</strong>). Cada noche repasa lo observado: lo que se repite o es importante pasa a permanente y lo demás se olvida.
      Lo que aprende leyendo correos o webs nunca pasa solo: te lo propone.</p>
    <div class="form-actions" style="margin:0 0 14px"><button class="btn" type="button" data-consolidate>${icon("refresh-cw", "sm")} Consolidar ahora</button></div>
    <h3 class="memory-h">Permanente</h3><div data-long>${skeleton(3)}</div>
    <h3 class="memory-h">En observación</h3><div data-notes>${skeleton(3)}</div>
    <h3 class="memory-h">Diario de consolidación</h3><div data-runs>${skeleton(1)}</div>`;

  const longBox = $(box, "[data-long]"), notesBox = $(box, "[data-notes]"), runsBox = $(box, "[data-runs]");
  const refresh = () => Promise.all([renderLong(), renderNotes(), renderRuns()]);

  const renderLong = () => load(longBox, {
    placeholder: skeleton(3),
    fetch: () => api.memories(),
    render: ({ items }) => {
      longBox.innerHTML = items.length ? `<div class="list">${items.map((m) => `<div class="row"><div class="row-main"><span>${esc(m.text)}</span>
        <span class="secondary">${KIND[m.kind]} · ${SOURCE[m.source]} · ${esc(relative(m.updatedAt))}</span></div>${dots(m.importance)}
        <button class="btn ghost icon-only" type="button" data-forget="${m.id}" aria-label="Olvidar: ${esc(m.text)}">${icon("trash-2", "sm")}</button></div>`).join("")}</div>`
        : empty("history", "Todavía no sabe nada de ti", "Cuando le cuentes cómo te gustan las cosas, o lo vaya notando, aparecerá aquí.");
      $$<HTMLButtonElement>(longBox, "[data-forget]").forEach((b) => b.addEventListener("click", async () => {
        b.disabled = true;
        try { await api.forget(b.dataset.forget!); toast("Olvidado."); void renderLong(); }
        catch (err) { b.disabled = false; toast(errorText(err), "error"); }
      }));
    },
  });

  const noteRow = (n: MemoryNote) => `<div class="row note ${n.status}">
    <div class="row-main"><span>${esc(n.text)}</span>
      <span class="secondary">${KIND[n.kind]} · ${n.mentions > 1 ? `${n.mentions} veces` : "una vez"}${n.daysSeen > 1 ? ` en ${n.daysSeen} días` : ""}${n.recallCount ? ` · consultada ${n.recallCount}` : ""} · ${esc(relative(n.lastSeenAt))}</span>
      ${n.status === "proposed" || n.provenance === "external" ? `<span class="secondary warn-text">${icon("shield", "sm")} Lo aprendió leyendo un correo, una web o un adjunto: decide tú.</span>`
        : `<span class="progress" role="meter" aria-valuemin="0" aria-valuemax="1" aria-valuenow="${n.score}" aria-label="Cerca de ser permanente: ${Math.round(n.score * 100)} %"><i style="width:${Math.round(n.score * 100)}%"></i></span>`}
    </div>
    <button class="btn ghost" type="button" data-keep="${n.id}" aria-label="Recordar siempre: ${esc(n.text)}">${icon("check", "sm")} Recordar</button>
    <button class="btn ghost icon-only" type="button" data-drop="${n.id}" aria-label="Descartar: ${esc(n.text)}">${icon("x", "sm")}</button></div>`;

  const renderNotes = () => load(notesBox, {
    placeholder: skeleton(3),
    fetch: () => api.memoryNotes(),
    render: ({ items }) => {
      const proposed = items.filter((n) => n.status === "proposed");
      const watching = items.filter((n) => n.status !== "proposed").sort((a, b) => b.score - a.score);
      notesBox.innerHTML = items.length ? `
        ${proposed.length ? `<p class="memory-sub">Por confirmar</p><div class="list">${proposed.map(noteRow).join("")}</div>` : ""}
        ${watching.length ? `<p class="memory-sub">Notado estos días (la barra indica lo cerca que está de ser permanente)</p><div class="list">${watching.map(noteRow).join("")}</div>` : ""}`
        : empty("eye", "Nada en observación", "Lo que vaya notando en vuestras conversaciones aparecerá aquí antes de ser permanente.");
      $$<HTMLButtonElement>(notesBox, "[data-keep]").forEach((b) => b.addEventListener("click", async () => {
        b.disabled = true;
        try { await api.keepNote(b.dataset.keep!); toast("Lo recordará siempre."); void refresh(); }
        catch (err) { b.disabled = false; toast(errorText(err), "error"); }
      }));
      $$<HTMLButtonElement>(notesBox, "[data-drop]").forEach((b) => b.addEventListener("click", async () => {
        b.disabled = true;
        try { await api.dropNote(b.dataset.drop!); toast("Descartado."); void renderNotes(); }
        catch (err) { b.disabled = false; toast(errorText(err), "error"); }
      }));
    },
  });

  const runHtml = (r: MemoryRun, open: boolean) => `<details class="run"${open ? " open" : ""}><summary><strong>${r.trigger === "nightly" ? "Esta noche" : "A petición"}</strong>
    · ${esc(relative(r.ranAt))} · ${r.promoted + r.merged + r.superseded} a permanente, ${r.proposed} por confirmar, ${r.faded} olvidadas${r.mode === "model" ? "" : " · sin modelo"}</summary>
    <ul>${r.diary.map((d) => `<li>${esc(d)}</li>`).join("")}</ul></details>`;
  const renderRuns = () => load(runsBox, {
    placeholder: skeleton(1),
    fetch: () => api.memoryRuns(),
    render: ({ items }) => {
      runsBox.innerHTML = items.length ? items.slice(0, 5).map((r, i) => runHtml(r, i === 0)).join("") : `<p class="ink-2">Aún no ha consolidado nada. Lo hace cada noche, o pulsa «Consolidar ahora».</p>`;
    },
  });

  const button = $<HTMLButtonElement>(box, "[data-consolidate]");
  button.addEventListener("click", async () => {
    const done = busy(button, "Consolidando…");
    try {
      const r = await api.consolidate();
      toast(`${r.promoted + r.merged + r.superseded} a permanente, ${r.proposed} por confirmar, ${r.faded} olvidadas.`);
      await refresh();
    } catch (err) { toast(errorText(err), "error"); }
    finally { done(); }
  });
  void refresh();
  return refresh;
}
